import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import { Mic, Loader2 } from "lucide-react";
import axios from "axios";
import Select from "react-select";
import { iotControlData as tvControlData, translations as tvTranslations } from "../data/NewControlData";
import { iotControlData } from "../data/iotControlData";
import { useLanguage } from "../language/LanguageContext";
import citiesData from "../data/country.json";
import UploadedAudiosModal from './UploadedAudiosModal';
import { ServerUrl } from "../config";
import { decodedToken } from "./utils";

// Function to resample audio buffer to 16kHz
const resampleAudio = async (audioBuffer, targetSampleRate = 16000) => {
  const sourceSampleRate = audioBuffer.sampleRate;

  if (sourceSampleRate === targetSampleRate) {
    return audioBuffer;
  }

  const offlineContext = new OfflineAudioContext(
    audioBuffer.numberOfChannels,
    (audioBuffer.length * targetSampleRate) / sourceSampleRate,
    targetSampleRate
  );

  const bufferSource = offlineContext.createBufferSource();
  bufferSource.buffer = audioBuffer;
  bufferSource.connect(offlineContext.destination);
  bufferSource.start(0);

  const resampledBuffer = await offlineContext.startRendering();
  return resampledBuffer;
};

// Function to convert AudioBuffer to WAV Blob
const audioBufferToWavBlob = (audioBuffer) => {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const length = audioBuffer.length * numChannels * 2 + 44;
  const buffer = new ArrayBuffer(length);
  const view = new DataView(buffer);

  let offset = 0;

  const writeString = (str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset++, str.charCodeAt(i));
    }
  };

  writeString("RIFF");
  view.setUint32(4, 36 + audioBuffer.length * numChannels * 2, true);
  writeString("WAVE");
  writeString("fmt ");
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, numChannels, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;
  view.setUint32(offset, sampleRate * numChannels * 2, true);
  offset += 4;
  view.setUint16(offset, numChannels * 2, true);
  offset += 2;
  view.setUint16(offset, 16, true);
  offset += 2;
  writeString("data");
  view.setUint32(offset, audioBuffer.length * numChannels * 2, true);
  offset += 4;

  for (let i = 0; i < audioBuffer.length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(channel)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: "audio/wav" });
};

function AudioUpload({ emailUser }) {
  const { language, setLanguage, t } = useLanguage();
  const decoded = decodedToken();
  const loggedInEmail = decoded?.email || emailUser || "";
  
  const [audioFile, setAudioFile] = useState(null);
  const [uploadaudioFile, setUploadAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [languageCode, setLanguageCode] = useState("en");
  const [gender, setGender] = useState("");
 
  const [age, setAge] = useState("");
  const [audiotext, setAudiotext] = useState("");
  const [userInputText, setUserInputText] = useState("");
  const [transcribedText, setTranscribedText] = useState("");
  const [translationText, settranslationText] = useState("");
  const [editableTranscribedText, setEditableTranscribedText] = useState("");

  const [transcribedStatus, setTranscribedStatus] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [mode, setMode] = useState("record");
  const [isRecording, setIsRecording] = useState(false);
  const [uploaderEmail, setUploaderEmail] = useState(loggedInEmail);
  const [channelOptions, setChannelOptions] = useState([]);
  const [isFetchingChannels, setIsFetchingChannels] = useState(false);
  const [isAudiosModalOpen, setIsAudiosModalOpen] = useState(false);
  const fileInputRef = useRef();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [voiceControlOptions, setVoiceControlOptions] = useState([]);
  const [selectedVoiceControl, setSelectedVoiceControl] = useState("");
  const [sampleUtterances, setSampleUtterances] = useState([]);
  const [selectedUtterance, setSelectedUtterance] = useState("");
  const [channelName, setChannelName] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [customCountry, setCustomCountry] = useState(""); // State for custom country input
  const [useCustomCountry, setUseCustomCountry] = useState(false); 

  const [uploadedAudios, setUploadedAudios] = useState([]);


  const languageMap = {
    english: "en",
    dutch: "nl",
    french: "fr",
    german: "de",
    italian: "it",
    spanish: "es",
  };

  // Updated countryOptions to include all countries without language filtering
  const countryOptions = [
    ...new Set(citiesData.cities.map((city) => city.country)),
  ].map((country) => ({
    value: country,
    label: country,
  }));
  

  const getTranscriptionLanguageCode = (code) => {
    const transcriptionMap = {
      en: "en",
      nl: "nl",
      fr: "fr",
      de: "de",
      it: "it",
      es: "es"
    };
    return transcriptionMap[code] || "en";
  };

  const categories = Object.keys(tvControlData);

  const categoryTextColors = {
    "Lighting Control": "#111827",
    "Lighting preset colors change": "#111827",
    "Switch/Plug/Socket/Outlet control": "#111827",
    "Thermostat Temperature control": "#111827",
    "Fan Control": "#111827",
    "Air Purifier control": "#111827",
    "Blinds, Curtain, shades control": "#111827",
  };



  // Load saved user details and category/voice control from localStorage on mount
  useEffect(() => {
    const savedUserDetails = JSON.parse(localStorage.getItem("userDetails"));
    if (savedUserDetails) {
      setLanguageCode(savedUserDetails.languageCode || "en");
      setLanguage(savedUserDetails.languageCode || "en");
      setGender(savedUserDetails.gender || "");
      setAge(savedUserDetails.age || "");
      setUploaderEmail(loggedInEmail || savedUserDetails.uploaderEmail || "");
      setSelectedCategory(savedUserDetails.selectedCategory || "");
      setSelectedVoiceControl(savedUserDetails.selectedVoiceControl || "");
    }
  }, []);

  // Fetch the user's uploaded audios so we can track which sample utterances are done
  const fetchUploadedAudios = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}audios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setUploadedAudios(response.data.data || []);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setUploadedAudios([]);
      }
      // Silently ignore other errors; tracking is a non-critical enhancement
    }
  };

  useEffect(() => {
    fetchUploadedAudios();
  }, []);

  // Fetch channels when category and voice control are selected
  useEffect(() => {
    if (selectedCategory === "Channel control" && selectedVoiceControl === "Change Channel by Name") {
      const fetchChannels = async () => {
        setIsFetchingChannels(true);
        try {
          const response = await axios.get(`${ServerUrl}all_channels`);
          if (response.status === 200) {
            const channels = response.data.data.map((channel) => ({
              value: channel.channelName,
              label: `${channel.channelName} (${channel.languageCode})`,
            }));
            setChannelOptions(channels);
          } else {
            throw new Error(t("Failed to fetch channels"));
          }
        } catch (error) {
          Swal.fire("Error", error.message || t("Failed to fetch channels"), "error");
        } finally {
          setIsFetchingChannels(false);
        }
      };
      fetchChannels();
    } else {
      setChannelOptions([]);
      setChannelName("");
    }
  }, [selectedCategory, selectedVoiceControl, t]);

  // Update voice control options when category changes
  useEffect(() => {
    if (selectedCategory && selectedCategory === "IoT voice control") {
      setVoiceControlOptions(Object.keys(iotControlData));
      if (!iotControlData[selectedVoiceControl]) {
        setSelectedVoiceControl("");
        setSampleUtterances([]);
        setSelectedUtterance("");
        setAudiotext("");
      }
    } else if (selectedCategory && tvControlData[selectedCategory]) {
      // Get unique voice control options (removes duplicates)
      const voiceControls = Object.keys(tvControlData[selectedCategory]);
      const uniqueVoiceControls = [...new Set(voiceControls)];
      setVoiceControlOptions(uniqueVoiceControls);
      if (!tvControlData[selectedCategory][selectedVoiceControl]) {
        setSelectedVoiceControl("");
        setSampleUtterances([]);
        setSelectedUtterance("");
        setAudiotext("");
        setChannelName("");
        setSelectedCountry("");
        setCustomCountry("");
        setUseCustomCountry(false);
      }
    } else {
      setVoiceControlOptions([]);
      setSelectedVoiceControl("");
      setSampleUtterances([]);
      setSelectedUtterance("");
      setAudiotext("");
      setChannelName("");
      setSelectedCountry("");
      setCustomCountry("");
      setUseCustomCountry(false);
    }
  }, [selectedCategory]);

  // Update sample utterances when voice control or language changes
  useEffect(() => {
    if (selectedCategory && selectedVoiceControl && tvControlData[selectedCategory][selectedVoiceControl]) {
      const langKey =
        language === "en" ? "english" :
        language === "nl" ? "dutch" :
        language === "fr" ? "french" :
        language === "de" ? "german" :
        language === "it" ? "italian" :
        language === "es" ? "spanish" : "english";
      const utterances = tvControlData[selectedCategory][selectedVoiceControl][langKey] ||
        tvControlData[selectedCategory][selectedVoiceControl]["spanish"] ||
        tvControlData[selectedCategory][selectedVoiceControl]["english"];
      setSampleUtterances(utterances || []);
      setSelectedUtterance("");
      setAudiotext("");
      setChannelName("");
      setSelectedCountry("");
    } else {
      setSampleUtterances([]);
      setSelectedUtterance("");
      setAudiotext("");
      setChannelName("");
      setSelectedCountry("");
    }
  }, [selectedCategory, selectedVoiceControl, language]);




  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    let newLanguage;
    switch (selectedLanguage) {
      case "english": newLanguage = "en"; break;
      case "dutch": newLanguage = "nl"; break;
      case "french": newLanguage = "fr"; break;
      case "german": newLanguage = "de"; break;
      case "italian": newLanguage = "it"; break;
      case "spanish": newLanguage = "es"; break;
      default: newLanguage = "en";
    }
    setLanguage(newLanguage);
    const langKey = selectedLanguage;
    setLanguageCode(languageMap[langKey] || "");
    setSelectedCategory("");
    setSelectedVoiceControl("");
    setSampleUtterances([]);
    setSelectedUtterance("");
    setAudiotext("");
    setChannelName("");
    setSelectedCountry("");
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setChannelName("");
    setSelectedCountry("");
    setCustomCountry("");
    setUseCustomCountry(false);
  };

  const handleVoiceControlChange = (e) => {
    setSelectedVoiceControl(e.target.value);
    setChannelName("");
    setSelectedCountry("");
    setCustomCountry("");
    setUseCustomCountry(false);
  };

  const handleChannelSelect = (selectedOption) => {
    setChannelName(selectedOption ? selectedOption.value : "");
  };

  const handleUtteranceClick = (utterance) => {
    setSelectedUtterance(utterance);
    setAudiotext(utterance);
    setChannelName("");
    setSelectedCountry("");
  };

  const handleCountrySelect = (selectedOption) => {
    if (selectedOption) {
      setSelectedCountry(selectedOption.value);
      setUseCustomCountry(false);
      setCustomCountry("");
    } else {
      setSelectedCountry("");
      setUseCustomCountry(true);
    }
  };

  const handleCustomCountryChange = (e) => {
    setCustomCountry(e.target.value);
    setSelectedCountry(e.target.value);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert("File size should be 10MB or less.");
        return;
      }
      const fileExtension = file.name.split(".").pop();
      if (fileExtension !== "mp3" && fileExtension !== "wav") {
        alert("Only .mp3 or .wav files are allowed.");
        return;
      }

      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const arrayBuffer = await file.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const resampledBuffer = await resampleAudio(audioBuffer, 16000);
        const resampledBlob = audioBufferToWavBlob(resampledBuffer);
        const resampledFile = new File([resampledBlob], file.name.replace(/\.[^.]+$/, ".wav"), { type: "audio/wav" });

        setAudioFile(resampledFile);
        setUploadAudioFile(file);
        setAudioPreview(URL.createObjectURL(file));
        setTranscribedText("");
        settranslationText("");
        setEditableTranscribedText("");
        setTranscribedStatus(null);

        if (languageCode) await transcribeAudio(resampledFile);
        else Swal.fire("Warning", t("Please select a language before uploading an audio file."), "warning");
      } catch (error) {
        Swal.fire("Error", t("Failed to process audio file: ") + error.message, "error");
      }
    }
  };






  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorderRef.current.ondataavailable = (event) => audioChunksRef.current.push(event.data);
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        const audioFileUpload = new File([audioBlob], "recorded-audio.wav", { type: "audio/wav" });
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const resampledBuffer = await resampleAudio(audioBuffer, 16000);
        const resampledBlob = audioBufferToWavBlob(resampledBuffer);
        const audioFile = new File([resampledBlob], "recorded-audio.wav", { type: "audio/wav" });

        setAudioFile(audioFile);
        setUploadAudioFile(audioFileUpload);
        setAudioPreview(URL.createObjectURL(audioBlob));
        setTranscribedText("");
        settranslationText("");
        setEditableTranscribedText("");
        setTranscribedStatus(null);
        stream.getTracks().forEach((track) => track.stop());

        if (languageCode) await transcribeAudio(audioFile);
        else Swal.fire("Warning", t("Please select a language before recording audio."), "warning");
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      Swal.fire("Error", t("Failed to access microphone: ") + error.message, "error");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (file) => {
    setIsTranscribing(true);
    setUserInputText("");
    const formData = new FormData();
    formData.append("audio", file);
    formData.append("language", getTranscriptionLanguageCode(languageCode));
    try {
      const response = await axios.post(`${ServerUrl}api/stt/upload-audio`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.status === 200) {
        setTranscribedText(response.data.transcribedText.transcription);
        settranslationText(response.data.transcribedText.translation);
        setEditableTranscribedText(response.data.transcribedText.transcription);
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || t("Failed to transcribe audio"), "error");
    } finally {
      setIsTranscribing(false);
    }
  };





  const handleSave = async () => {
    if (!audioFile || !languageCode || !gender || !age || !transcribedText || !selectedVoiceControl) {
      Swal.fire("Error", t("Please fill in all fields and upload or record an audio file."), "error");
      return;
    }

    if (transcribedStatus === "not_ok" && !userInputText.trim()) {
      Swal.fire("Error", t("Please provide the user input when transcription is Not OK."), "error");
      return;
    }

    if (selectedCategory === "Channel control" && selectedVoiceControl === "Change Channel by Name" && !channelName.trim()) {
      Swal.fire("Error", t("Please enter a channel name."), "error");
      return;
    }

    if (selectedCategory === "Weather" && selectedVoiceControl === "Location-Specific Queries" && !selectedCountry.trim()) {
      Swal.fire("Error", t("Please select a country."), "error");
      return;
    }

    const formData = new FormData();
    formData.append("audio", uploadaudioFile);
    formData.append("email", uploaderEmail);
    formData.append("uploaderEmail", uploaderEmail);
    formData.append("audiotext", selectedVoiceControl || "na");
    formData.append("sampleUtterance", selectedUtterance || "");
    formData.append("audioLabel", `${gender} - ${age}`);
    formData.append("audioLanguage",
      language === "en" ? "english" :
      language === "nl" ? "dutch" :
      language === "fr" ? "french" :
      language === "de" ? "german" :
      language === "it" ? "italian" :
      language === "es" ? "spanish" : "");
    formData.append("languageCode", language);
    formData.append("transcribedText", transcribedText);
    formData.append("userInputText", transcribedStatus === "ok" ? "" : userInputText);
    formData.append("transcribedStatus", transcribedStatus);
    formData.append("translation", translationText);
    formData.append("ChannelName", selectedCategory === "Channel control" && selectedVoiceControl === "Change Channel by Name" ? channelName : "");
    formData.append("Country", selectedCategory === "Weather" && selectedVoiceControl === "Location-Specific Queries" ? selectedCountry : "na");
    try {
      const response = await axios.post(`${ServerUrl}uploads`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.status === 200 || response.status === 201) {
        const userDetails = {
          languageCode,
          gender,
          age,
          uploaderEmail,
          selectedCategory,
          selectedVoiceControl,
        };
        localStorage.setItem("userDetails", JSON.stringify(userDetails));
        Swal.fire("Success", t("Audio successfully uploaded!"), "success");
        fetchUploadedAudios();
        handleDeleteUploadedFile();
      }
    } catch (error) {
      Swal.fire("Error", error.message || t("Something went wrong"), "error");
    }
  };

  const handleDeleteUploadedFile = () => {
    if (audioPreview) URL.revokeObjectURL(audioPreview);
    setAudioFile(null);
    setUploadAudioFile(null);
    setAudioPreview(null);
    setAudiotext("");
    setTranscribedText("");
    settranslationText("");
    setEditableTranscribedText("");
    setUserInputText("");
    setTranscribedStatus(null);
    setSelectedUtterance("");
    setChannelName("");
    setSelectedCountry("");
    setCustomCountry("");
    setUseCustomCountry(false);
    setIsRecording(false);
    if (mode === "upload" && fileInputRef.current) fileInputRef.current.value = null;
  };

  const getVoiceControlStats = (option) => {
    if (!selectedCategory || !tvControlData[selectedCategory] || !tvControlData[selectedCategory][option]) {
      return { submitted: 0, total: 0 };
    }

    const optionData = tvControlData[selectedCategory][option];
    const langKey =
      language === "en" ? "english" :
      language === "nl" ? "dutch" :
      language === "fr" ? "french" :
      language === "de" ? "german" :
      language === "it" ? "italian" :
      language === "es" ? "spanish" : "english";

    const utterances = optionData[langKey] || optionData["spanish"] || optionData["english"] || [];
    const total = utterances.length;

    const currentLanguageName =
      language === "en" ? "english" :
      language === "nl" ? "dutch" :
      language === "fr" ? "french" :
      language === "de" ? "german" :
      language === "it" ? "italian" :
      language === "es" ? "spanish" : "english";

    // Count how many unique utterances for this option under the selected language have been submitted so far
    const submittedSet = new Set(
      uploadedAudios
        .filter(
          (audio) =>
            audio.audiotext === option &&
            audio.audioLanguage === currentLanguageName &&
            audio.sampleUtterance
        )
        .map((audio) => audio.sampleUtterance)
    );
    const submitted = utterances.filter(u => submittedSet.has(u)).length;

    return { submitted, total };
  };

  const getTranslatedText = (key, type) => {
    const langKey =
      language === "en" ? "english" :
      language === "nl" ? "dutch" :
      language === "fr" ? "french" :
      language === "de" ? "german" :
      language === "it" ? "italian" :
      language === "es" ? "spanish" : "english";
    const translationMap = type === "category" ? tvTranslations.categories : tvTranslations.voiceControls;
    return translationMap[key]?.[langKey] || translationMap[key]?.["spanish"] || translationMap[key]?.["english"] || key;
  };

  const handleModeChange = (newMode) => setMode(mode === newMode ? "" : newMode);

  // Determine which sample utterances have already been uploaded for the
  // currently selected voice control and language.
  const currentLanguageName =
    language === "en" ? "english" :
    language === "nl" ? "dutch" :
    language === "fr" ? "french" :
    language === "de" ? "german" :
    language === "it" ? "italian" :
    language === "es" ? "spanish" : "english";

  const uploadedUtteranceSet = new Set(
    uploadedAudios
      .filter(
        (audio) =>
          audio.audiotext === selectedVoiceControl &&
          audio.audioLanguage === currentLanguageName &&
          audio.sampleUtterance
      )
      .map((audio) => audio.sampleUtterance)
  );

  const uploadedUtteranceCount = sampleUtterances.filter((u) =>
    uploadedUtteranceSet.has(u)
  ).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="mb-4 w-full">
          <label htmlFor="language-select" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            {t("Select Language")}
          </label>
          <select
            id="language-select"
            value={
              language === "en" ? "english" :
              language === "nl" ? "dutch" :
              language === "fr" ? "french" :
              language === "de" ? "german" :
              language === "it" ? "italian" :
              language === "es" ? "spanish" : ""
            }
            onChange={handleLanguageChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
          >
            <option value="">{t("Select Language")}</option>
            <option value="english">English (EN)</option>
            <option value="dutch">Nederlands (NL)</option>
            <option value="french">Français (FR)</option>
            <option value="german">Deutsch (DE)</option>
            <option value="italian">Italiano (IT)</option>
            <option value="spanish">Español (ES)</option>
          </select>
        </div>

        <div className="mb-4 w-full">
          <label htmlFor="gender-select" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            {t("Select Gender")}
          </label>
          <select
            id="gender-select"
            value={gender}
            onChange={(e) => { setGender(e.target.value); setAge(""); }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
          >
            <option value="">{t("Select Gender")}</option>
            <option value="Female">{t("Female")}</option>
            <option value="Male">{t("Male")}</option>
          </select>
        </div>

      <div className="mb-4 w-full sm:col-span-2">
        <label htmlFor="age-select" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
          {t("Select Age Category")}
        </label>
            <select
              id="age-select"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
            >
              <option value="">{t("Select Age Category")}</option>
              <option value="Kid">{t("Kid")}</option>
              <option value="Adult">{t("Adult")}</option>
              <option value="Older">{t("Older")}</option>
            </select>
          </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t("Category")}</label>
          <select
            value={selectedCategory}
            onChange={handleCategoryChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
          >
            <option value="">{t("Select Category")}</option>
            {categories.map((category) => (
              <option 
                key={category} 
                value={category}
                style={{ 
                  color: categoryTextColors[category] || '#374151',
                  fontWeight: '600' 
                }}
              >
                {getTranslatedText(category, "category")}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t("Voice Control")}</label>
          <select
            value={selectedVoiceControl}
            onChange={handleVoiceControlChange}
            disabled={!selectedCategory}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
          >
            <option value="">{t("Select Voice Control")}</option>
            {voiceControlOptions.map((option) => {
              const { submitted, total } = getVoiceControlStats(option);
              let optionStyle = { color: "#000000" };
              if (total > 0) {
                if (submitted === total) {
                  optionStyle = { backgroundColor: "#dcfce7", color: "#166534" };
                } else if (submitted > 0 && submitted < total) {
                  optionStyle = { backgroundColor: "#ffedd5", color: "#9a3412" };
                }
              }
              return (
                <option key={option} value={option} style={optionStyle}>
                  {getTranslatedText(option, "voiceControl")} ({submitted}/{total} {t("uploaded")})
                </option>
              );
            })}
          </select>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            {t("Sample Utterances")}
            {selectedVoiceControl && sampleUtterances.length > 0 && (
              <span
                className={`ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ${
                  uploadedUtteranceCount === sampleUtterances.length
                    ? "bg-green-100 text-green-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {uploadedUtteranceCount} / {sampleUtterances.length} {t("uploaded")}
              </span>
            )}
          </label>
          <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-lg bg-gray-50 max-h-32 overflow-y-auto">
            {sampleUtterances.length > 0 ? (
              sampleUtterances.map((utterance, index) => {
                const isUploaded = uploadedUtteranceSet.has(utterance);
                const isSelected = selectedUtterance === utterance;
                return (
                  <div
                    key={index}
                    onClick={() => handleUtteranceClick(utterance)}
                    title={isUploaded ? t("Already uploaded") : t("Not uploaded yet")}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs cursor-pointer shadow-sm border ${
                      isSelected
                        ? "bg-blue-500 text-white border-blue-500"
                        : isUploaded
                        ? "bg-green-100 text-green-800 border-green-300 hover:bg-green-200"
                        : "bg-white text-gray-700 hover:bg-blue-100 border-gray-200"
                    }`}
                  >
                    {isUploaded && <span className="font-bold">✓</span>}
                    {utterance}
                  </div>
                );
              })
            ) : (
              <p className="text-xs text-gray-500">{t("No utterances available")}</p>
            )}
          </div>
        </div>
        {/* Allowed Devices Section - Only for IoT voice control */}
        {selectedCategory === "IoT voice control" && selectedVoiceControl && iotControlData[selectedVoiceControl]?.allowedDevices && (
          <div className="sm:col-span-3">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              {t("Allowed Devices")}
            </label>
            <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-lg bg-gray-50 max-h-32 overflow-y-auto">
              {iotControlData[selectedVoiceControl].allowedDevices.length > 0 ? (
                iotControlData[selectedVoiceControl].allowedDevices.map((device, index) => (
                  <div
                    key={index}
                    className="px-3 py-1 rounded-full text-xs bg-green-100 text-green-800 border border-green-300 shadow-sm"
                  >
                    {device}
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">{t("No devices specified")}</p>
              )}
            </div>
          </div>
        )}

      </div>

      {selectedCategory === "Channel control" && selectedVoiceControl === "Change Channel by Name" && (
        <div className="mb-4 w-full">
          <label htmlFor="channel-select" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            {t("Search Channels")}
          </label>
          <Select
            id="channel-select"
            options={channelOptions}
            onChange={handleChannelSelect}
            placeholder={t("Search for a channel...")}
            isClearable
            isLoading={isFetchingChannels}
            className="text-xs sm:text-sm"
            classNamePrefix="react-select"
          />
          <label htmlFor="channel-name" className="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-1 mt-4">
            <span>{t("Channel Name")}</span>
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            id="channel-name"
            type="text"
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
            placeholder={t("Enter channel name (e.g., BBC, HBO)")}
            required
          />
        </div>
      )}

      {selectedCategory === "Weather" && selectedVoiceControl === "Location-Specific Queries" && (
        <div className="mb-4 w-full">
          <label htmlFor="country-select" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            {t("Select or Enter Country")} <span className="text-red-500">*</span>
          </label>
          <Select
            id="country-select"
            options={countryOptions}
            onChange={handleCountrySelect}
            placeholder={t("Select a country...")}
            isClearable
            className="text-xs sm:text-sm"
            classNamePrefix="react-select"
          />
          {useCustomCountry && (
            <div className="mt-2">
              <label htmlFor="custom-country" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                {t("Enter Custom Country")}
              </label>
              <input
                id="sustom-country"
                type="text"
                value={customCountry}
                onChange={handleCustomCountryChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                placeholder={t("Enter country name")}
              />
            </div>
          )}
        </div>
      )}

      <div className="mb-4">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{t("Select Audio Input Method")}</label>
        <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-2 sm:space-y-0">
          <label className="flex items-center text-xs sm:text-sm">
            <input type="checkbox" checked={mode === "upload"} onChange={() => handleModeChange("upload")} className="mr-2" />
            {t("Upload Audio")}
          </label>
          <label className="flex items-center text-xs sm:text-sm">
            <input type="checkbox" checked={mode === "record"} onChange={() => handleModeChange("record")} className="mr-2" />
            {t("Record Audio")}
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="mb-4 w-full sm:col-span-2">
          {mode === "upload" ? (
            <>
              <label htmlFor="file-input" className="block text-sm font-medium text-gray-700 mb-1">{t("Upload Audio")}</label>
              <div className="flex items-center space-x-4 border border-gray-300 rounded-lg p-2">
                <input
                  ref={fileInputRef}
                  id="file-input"
                  type="file"
                  accept=".mp3,.wav"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white text-xs sm:text-sm transition"
                >
                  {t("Choose File")}
                </button>
                <span className="text-sm text-gray-600">
                  {audioFile ? audioFile.name : t("No file chosen")}
                </span>
              </div>
            </>
          ) : mode === "record" ? (
            <div className="flex flex-col items-center">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("Record Audio")}</label>
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`flex items-center justify-center px-4 py-2 rounded text-white text-sm ${
                  isRecording ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"
                }`}
                disabled={isTranscribing}
              >
                {isRecording ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t("Stop")}
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5 mr-2" />
                    {t("Start Audio")}
                  </>
                )}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {audioPreview && (
        <div className="mb-4 w-full flex justify-center items-center overflow-hidden rounded-lg border border-gray-300 p-4 relative">
          <audio controls src={audioPreview} className="w-full max-w-md" />
          <button
            onClick={handleDeleteUploadedFile}
            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
          >
            X
          </button>
        </div>
      )}

      {isTranscribing && (
        <div className="flex justify-center items-center mb-4">
          <div className="loader"></div>
          <span className="ml-2 text-gray-700">{t("Transcribing")}</span>
        </div>
      )}

      {transcribedText && (
        <div className="mb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("AI Transcribed Text")}</label>
            <p className="p-2 border border-gray-300 rounded-lg bg-gray-50 text-sm">{transcribedText}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("Transcription Status")}</label>
            <div className="mt-2 flex space-x-4">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={transcribedStatus === "ok"}
                  onChange={() => {
                    setTranscribedStatus(transcribedStatus === "ok" ? null : "ok");
                    setUserInputText("");
                  }}
                  disabled={!transcribedText}
                  className="mr-2"
                />
                {t("OK")}
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={transcribedStatus === "not_ok"}
                  onChange={() => {
                    const newStatus = transcribedStatus === "not_ok" ? null : "not_ok";
                    setTranscribedStatus(newStatus);
                    if (newStatus === "not_ok" && selectedUtterance && !userInputText) {
                      setUserInputText(selectedUtterance);
                    }
                  }}
                  disabled={!transcribedText}
                  className="mr-2"
                />
                {t("Not OK")}
              </label>
            </div>
          </div>

          {transcribedStatus === "not_ok" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("User Input")}
              </label>
              <textarea
                value={userInputText}
                onChange={(e) => setUserInputText(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                rows="2"
                placeholder={t("Enter the correct text here")}
              />
            </div>
          )}
        </div>
      )}

      {translationText && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("Translated Text")}</label>
          <p className="p-2 border border-gray-300 rounded-lg bg-gray-50 text-sm">{translationText}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:space-x-6 w-full">
        <button
          onClick={handleSave}
          className={`w-full py-3 rounded-lg mt-4 sm:mt-0 text-xs sm:text-sm text-white ${
            !audioFile ||
            !languageCode ||
            !gender ||
            !age ||
            !transcribedText ||
            !uploaderEmail ||
            isTranscribing ||
            (selectedCategory === "Channel control" && selectedVoiceControl === "Change Channel by Name" && !channelName.trim()) ||
            (selectedCategory === "Weather" && selectedVoiceControl === "Location-Specific Queries" && !selectedCountry.trim())
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-500 hover:bg-green-600"
          }`}
          disabled={
            !audioFile ||
            !languageCode ||
            !gender ||
            !age ||
            !transcribedText ||
            isTranscribing ||
            (selectedCategory === "Channel control" && selectedVoiceControl === "Change Channel by Name" && !channelName.trim()) ||
            (selectedCategory === "Weather" && selectedVoiceControl === "Location-Specific Queries" && !selectedCountry.trim())
          }
        >
          {t("Upload")}
        </button>
        <button
          onClick={() => setIsAudiosModalOpen(true)}
          className="w-full bg-blue-500 text-white py-3 rounded-lg mt-4 sm:mt-0 hover:bg-blue-600 text-xs sm:text-sm"
        >
          {t("Uploaded Audios")}
        </button>
      </div>
      
        <UploadedAudiosModal
          isOpen={isAudiosModalOpen}
          onClose={() => setIsAudiosModalOpen(false)}
          emailUser={emailUser}
        />
    </div>
  );
}

export default AudioUpload;